import React, { useState, useEffect, useCallback } from 'react';
import { getAllUsers, deleteUser, User } from '../user/services/usersAPI';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import GroupsIcon from '@mui/icons-material/Groups'; 
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UserFormModal from './components/UserFormModal';
import EditUserModal from './components/EditUserModal';
import ConfirmationModal from './components/ConfirmationModal'; 
import AssignClientsModal from './components/AssignClientsModal'; 
import styles from './AdminUsersPage.module.css';

const ITEMS_PER_PAGE = 20;

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAdmin, setFilterAdmin] = useState<'all' | 'admin' | 'user' | 'supervisor' | 'representante'>('user');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortField, setSortField] = useState<'ID_USUARIO' | 'NOME' | 'BASE'>('ID_USUARIO');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [deleteModalUser, setDeleteModalUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [copyUser, setCopyUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(data);
      // Já aplica o filtro comercial pra não mostrar todos os usuários de uma vez
      setFilteredUsers(data.filter(user =>
        (user.ID_VENDEDOR != null && String(user.ID_VENDEDOR).trim() !== '' && String(user.ID_VENDEDOR).trim() !== '0') ||
        (user.BASE != null && Number(user.BASE) !== 0)
      ));
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setCopyUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditUser(user);
  };

  const handleCopyUser = (user: User) => {
    setCopyUser(user);
    setIsModalOpen(true);
  };


  const handleModalSuccess = async () => {
    await loadUsers();
  };

  const handleDeleteClick = (user: User) => {
    setDeleteModalUser(user);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalUser) return;
    setIsDeleting(true);
    try {
      await deleteUser(deleteModalUser.ID_USUARIO);
      alert('Usuário excluído com sucesso!');
      await loadUsers();
      setDeleteModalUser(null);
    } catch (error: any) {
      console.error('Erro ao excluir usuário:', error);
      alert(error.response?.data?.error || 'Erro ao excluir usuário');
    } finally {
      setIsDeleting(false);
    }
  };

  /** Retorna true se o usuário tem dados comerciais (vendedor ou representante) */
  const temDadosComerciais = (user: User): boolean => {
    // ID_VENDEDOR pode vir como string vazia '', string '0', número 0, ou null
    const vendedor = user.ID_VENDEDOR;
    const temVendedor = vendedor != null
      && String(vendedor).trim() !== ''
      && String(vendedor).trim() !== '0';

    // BASE pode vir como 0, null, ou um número válido
    const base = user.BASE;
    const temBase = base != null && Number(base) !== 0;

    return temVendedor || temBase;
  };

  const filterUsers = useCallback(() => {
    let filtered = [...users];

    if (filterAdmin === 'admin') {
      // Admin: mostra todos os administradores
      filtered = filtered.filter(user => user.UsuarioAdm === 1);
    } else if (filterAdmin === 'user') {
      // Vendedores: só quem tem ID_VENDEDOR ou BASE (vendedores/representantes)
      filtered = filtered.filter(user =>
        user.UsuarioAdm !== 1 && temDadosComerciais(user)
      );
    } else if (filterAdmin === 'supervisor') {
      // Supervisores: UsuarioSupervisor = 1
      filtered = filtered.filter(user => user.UsuarioSupervisor === 1);
    } else if (filterAdmin === 'representante') {
      // Representantes: tem ID_VENDEDOR mas não tem BASE
      filtered = filtered.filter(user =>
        user.ID_VENDEDOR != null && String(user.ID_VENDEDOR).trim() !== '' && String(user.ID_VENDEDOR).trim() !== '0' &&
        (user.BASE == null || user.BASE === 0)
      );
    } else {
      // 'all': mostra todos os usuários (admins, diretores, supervisores, vendedores)
      // Sem filtro comercial — todo mundo aparece
      filtered = [...users];
    }

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.NOME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.USUARIO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.ID_USUARIO?.toString().includes(searchTerm)
      );
    }

    // Ordenação: padrão ID mais recente primeiro
    filtered.sort((a, b) => {
      let valA: any, valB: any;
      if (sortField === 'ID_USUARIO') {
        valA = a.ID_USUARIO;
        valB = b.ID_USUARIO;
      } else if (sortField === 'NOME') {
        valA = a.NOME?.toLowerCase() || '';
        valB = b.NOME?.toLowerCase() || '';
      } else {
        valA = a.BASE || 0;
        valB = b.BASE || 0;
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, filterAdmin, sortField, sortDir]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getPaginationButtons = () => {
    const pageNumbers: (number | string)[] = [];
    const maxButtons = 5;
    const halfMax = Math.floor(maxButtons / 2);

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      if (currentPage > halfMax + 2) pageNumbers.push('...');

      const start = Math.max(2, currentPage - halfMax);
      const end = Math.min(totalPages - 1, currentPage + halfMax);

      for (let i = start; i <= end; i++) pageNumbers.push(i);

      if (currentPage < totalPages - halfMax - 1) pageNumbers.push('...');
      if (totalPages > 1) pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const handleSort = (field: 'ID_USUARIO' | 'NOME' | 'BASE') => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const resetSort = () => {
    setSortField('ID_USUARIO');
    setSortDir('desc');
  };

  const SortIcon = ({ field }: { field: 'ID_USUARIO' | 'NOME' | 'BASE' }) => {
    if (sortField !== field) return <span className={styles.sortIconInactive}>↕</span>;
    return sortDir === 'desc'
      ? <ArrowDownwardIcon className={styles.sortIcon} />
      : <ArrowUpwardIcon className={styles.sortIcon} />;
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainCard}>
        <div className={styles.cardHeader}>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por nome, usuário ou ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              disabled={loading}
            />
          </div>

          <div className={styles.actionsContainer}>
            <select
              value={filterAdmin}
              onChange={(e) => setFilterAdmin(e.target.value as any)}
              className={styles.filterSelect}
              disabled={loading}
            >
              <option value="all">Todos</option>
              <option value="admin">Admins</option>
              <option value="supervisor">Supervisores</option>
              <option value="representante">Representantes</option>
              <option value="user">Vendedores</option>
            </select>

            <button onClick={() => setIsTradeModalOpen(true)} className={styles.secondaryButton} disabled={loading}>
              <CompareArrowsIcon />
              <span>Transferir Clientes</span>
            </button>

            <button onClick={handleCreateUser} className={styles.primaryButton} disabled={loading}>
              <AddIcon />
              <span>Novo Usuário</span>
            </button>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          {currentUsers.length === 0 && !loading ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIconWrapper}>
                <SearchIcon />
              </div>
              <p>Nenhum usuário encontrado</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.sortableHeader} onClick={() => !loading && handleSort('ID_USUARIO')}>
                    ID <SortIcon field="ID_USUARIO" />
                  </th>
                  <th className={styles.sortableHeader} onClick={() => !loading && handleSort('NOME')}>
                    Usuário <SortIcon field="NOME" />
                  </th>
                  <th>ID Vendedor</th>
                  <th className={styles.sortableHeader} onClick={() => !loading && handleSort('BASE')}>
                    Base <SortIcon field="BASE" />
                  </th>
                  <th>CRM</th>
                  <th>Acesso</th>
                  <th align="right">
                    <button onClick={() => !loading && resetSort()} className={styles.resetSortBtn} title="Limpar ordenação" disabled={loading}>
                      ↺
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className={styles.skeletonRow}>
                      <td><div className={styles.skeletonText} style={{ width: '40px' }}></div></td>
                      <td>
                        <div className={styles.userInfo}>
                          <div className={`${styles.avatar} ${styles.skeletonAvatar}`}></div>
                          <div className={styles.userDetails}>
                            <div className={styles.skeletonText} style={{ width: '120px', height: '14px' }}></div>
                            <div className={styles.skeletonText} style={{ width: '80px', height: '10px', marginTop: '6px' }}></div>
                          </div>
                        </div>
                      </td>
                      <td><div className={styles.skeletonText} style={{ width: '50px' }}></div></td>
                      <td><div className={styles.skeletonText} style={{ width: '70px' }}></div></td>
                      <td><div className={styles.skeletonText} style={{ width: '50px' }}></div></td>
                      <td><div className={styles.skeletonText} style={{ width: '80px', height: '22px', borderRadius: '12px' }}></div></td>
                      <td>
                        <div className={styles.actionButtons}>
                          <div className={styles.skeletonIcon} style={{ width: '28px', height: '28px', borderRadius: '4px' }}></div>
                          <div className={styles.skeletonIcon} style={{ width: '28px', height: '28px', borderRadius: '4px' }}></div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  currentUsers.map(user => (
                    <tr key={user.ID_USUARIO} className={styles.tableRow}>
                      <td className={styles.idCell}>{user.ID_USUARIO}</td>
                      <td>
                        <div className={styles.userInfo}>
                          <div className={styles.avatar}>
                            {user.NOME ? user.NOME.charAt(0).toUpperCase() : <PersonIcon />}
                          </div>
                          <div className={styles.userDetails}>
                            <span className={styles.userName}>{user.NOME || user.USUARIO}</span>
                            <span className={styles.userLogin}>{user.USUARIO}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {user.ID_VENDEDOR && String(user.ID_VENDEDOR).trim() !== '0' ? (
                          <span className={styles.badgeVendedor}>{user.ID_VENDEDOR}</span>
                        ) : (
                          <span className={styles.badgeEmpty}>-</span>
                        )}
                      </td>
                      <td>
                        {user.BASE ? (
                          <span className={styles.badgeBase}>Base {user.BASE}</span>
                        ) : (
                          <span className={styles.badgeEmpty}>-</span>
                        )}
                      </td>
                      <td>
                        {user.usuarioCRM != null && String(user.usuarioCRM).trim() !== '' && String(user.usuarioCRM).trim() !== '0' ? (
                          <span className={styles.badgeCrm}>{user.usuarioCRM}</span>
                        ) : (
                          <span className={styles.badgeEmpty}>-</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const role = user.UsuarioAdm === 1 ? 'Admin'
                            : user.UsuarioDiretoria === 1 ? 'Diretor'
                            : user.UsuarioSupervisor === 1 ? 'Supervisor'
                            : temDadosComerciais(user) ? 'Vendedor'
                            : 'Usuário';
                          const roleClass = user.UsuarioAdm === 1 ? styles.badgeAdmin
                            : user.UsuarioDiretoria === 1 ? styles.badgeDiretor
                            : user.UsuarioSupervisor === 1 ? styles.badgeSupervisor
                            : temDadosComerciais(user) ? styles.badgeVendedor
                            : styles.badgeUser;
                          return (
                            <span className={`${styles.badgeRole} ${roleClass}`}>{role}</span>
                          );
                        })()}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button onClick={() => handleCopyUser(user)} className={styles.actionBtn} title="Copiar usuário">
                            <ContentCopyIcon />
                          </button>
                          <button onClick={() => handleEditUser(user)} className={styles.actionBtn} title="Editar usuário">
                            <EditIcon />
                          </button>
                          <button onClick={() => handleDeleteClick(user)} className={`${styles.actionBtn} ${styles.actionBtnDelete}`} title="Excluir usuário">
                            <DeleteIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <div className={styles.pageButtons}>
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon fontSize="small" />
                Anterior
              </button>

              <div className={styles.pageNumbers}>
                {getPaginationButtons().map((item, index) =>
                  item === '...' ? (
                    <span key={index} className={styles.pageEllipsis}>...</span>
                  ) : (
                    <button
                      key={index}
                      onClick={() => handlePageChange(item as number)}
                      className={`${styles.pageNumber} ${item === currentPage ? styles.pageNumberActive : ''}`}
                    >
                      {item}
                    </button>
                  ),
                )}
              </div>

              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Próxima
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          </div>
        )}
      </div>

      <UserFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setCopyUser(null); }}
        onSuccess={() => { handleModalSuccess(); setCopyUser(null); }}
        initialData={copyUser ? {
          nome: copyUser.NOME,
          usuario: copyUser.USUARIO,
          tipo: temDadosComerciais(copyUser)
            ? (copyUser.BASE != null && Number(copyUser.BASE) !== 0 ? 'vendedor' : 'representante')
            : 'comum',
          permissions: {
            UsuarioAdm: copyUser.UsuarioAdm,
            UsuarioVendas: copyUser.UsuarioVendas,
            Usuario_carteira: copyUser.Usuario_carteira,
            UsuarioDeshboard: copyUser.UsuarioDeshboard,
            UsuarioSupervisor: copyUser.UsuarioSupervisor,
            UsuarioDiretoria: copyUser.UsuarioDiretoria,
            usuarioAmostras: copyUser.usuarioAmostras,
            usuarioCabedal: copyUser.usuarioCabedal,
            usuarioCorte: copyUser.usuarioCorte,
            usuarioPlanos: copyUser.usuarioPlanos,
            UsuarioStrobel: copyUser.UsuarioStrobel,
            UsuarioIlhos: copyUser.UsuarioIlhos,
            UsuarioExpedicao: copyUser.UsuarioExpedicao,
            UsuarioMapas: copyUser.UsuarioMapas,
            UsuarioInjetora: copyUser.UsuarioInjetora,
            UsuarioPcp: copyUser.UsuarioPcp,
            UsuarioGerentePcp: copyUser.UsuarioGerentePcp,
            UsuarioEsteira: copyUser.UsuarioEsteira,
            UsuarioFechamento: copyUser.UsuarioFechamento,
            UsuarioEstoque: copyUser.UsuarioEstoque,
            UsuarioPcpFaturamento: copyUser.UsuarioPcpFaturamento,
            UsuarioContabiliza: copyUser.UsuarioContabiliza,
            UsuarioSetor: copyUser.UsuarioSetor,
            UsuarioControladoria: copyUser.UsuarioControladoria,
            SupervisorRegional: copyUser.SupervisorRegional,
            UsuarioGerente: copyUser.UsuarioGerente,
            UsuarioJJSolucoes: copyUser.UsuarioJJSolucoes,
            UsuarioConferencia: copyUser.UsuarioConferencia,
            UsuarioCurtume: copyUser.UsuarioCurtume,
            UsuarioQualidade: copyUser.UsuarioQualidade,
            UsuarioSac: copyUser.UsuarioSac,
            UsuarioProgPedidos: copyUser.UsuarioProgPedidos,
            UsuarioCarregamento: copyUser.UsuarioCarregamento,
            UsuarioContagemCabedal: copyUser.UsuarioContagemCabedal,
            UsuarioArquivos: copyUser.UsuarioArquivos,
            UsuarioLogistica: copyUser.UsuarioLogistica,
            portaria: copyUser.portaria,
            UsuarioEnderecos: copyUser.UsuarioEnderecos,
            UsuarioSegTrab: copyUser.UsuarioSegTrab,
            UsuarioControleLogistica: copyUser.UsuarioControleLogistica,
            AcessoAnalytics: copyUser.AcessoAnalytics,
            AcessoEntregas: copyUser.AcessoEntregas,
            AcessoVeiculos: copyUser.AcessoVeiculos,
            AcessoFrota: copyUser.AcessoFrota,
            AcessoVolumetria: copyUser.AcessoVolumetria,
          },
        } : undefined}
      />

      <ConfirmationModal
        isOpen={!!deleteModalUser}
        onClose={() => setDeleteModalUser(null)}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o usuário ${deleteModalUser?.NOME}?`}
        confirmText="Excluir"
        loading={isDeleting}
      />

      <EditUserModal
        isOpen={!!editUser}
        onClose={() => setEditUser(null)}
        onSuccess={loadUsers}
        user={editUser!}
      />

      <AssignClientsModal
        isOpen={isTradeModalOpen}
        onClose={() => setIsTradeModalOpen(false)}
        users={users}
      />
    </div>
  );
};

export default AdminUsersPage;